import { useEffect, useState } from "react";
import { rememberLocationLabels, unlabeledLocationTokens } from "@suresend/shared/connect/locationLabels";

// token -> the request asking for its label, shared by every island on the
// page: the filter panel and the map mount with the same tokens, and one
// request should serve both. An answered token stays here, so one ez doesn't
// know (the server answers "Selected city", which isn't learned) isn't asked
// for again on every render; a failed request is forgotten so it can be retried.
const requests = new Map();

function requestLabels(tokens, endpoint) {
  const fresh = tokens.filter((token) => !requests.has(`${endpoint}|${token}`));
  if (fresh.length) {
    const params = new URLSearchParams();
    fresh.forEach((token) => params.append("ids[]", token));
    const request = fetch(`${endpoint}?${params.toString()}`, { credentials: "same-origin" })
      .then((res) => res.json())
      .then((data) => rememberLocationLabels(data?.labels))
      .catch((error) => {
        fresh.forEach((token) => requests.delete(`${endpoint}|${token}`));
        throw error;
      });
    fresh.forEach((token) => requests.set(`${endpoint}|${token}`, request));
  }
  return Promise.all(tokens.map((token) => requests.get(`${endpoint}|${token}`)));
}

// Labels for location tokens the island holds without one. A gazetteer token
// ("City:<uuid>", EZH-3070) doesn't carry its name: the SSR seed and picked
// suggestions cover the usual cases, and this covers the rest (a visitor's saved
// search, a custom template's seed) via /_locations/labels. Degrade-safe — a
// failed fetch leaves the generic "Selected city" label in place.
//
// Returns a counter that bumps when labels land, so the island re-renders and
// reads them through `locationLabel(token)`.
export function useLocationLabels(tokens, { endpoint = "/_locations/labels" } = {}) {
  const [version, setVersion] = useState(0);
  const missingKey = unlabeledLocationTokens(tokens).sort().join("|");

  useEffect(() => {
    if (!missingKey) return undefined;
    let cancelled = false;
    requestLabels(missingKey.split("|"), endpoint)
      .then(() => { if (!cancelled) setVersion((v) => v + 1); })
      .catch(() => { /* keep the generic label */ });
    return () => { cancelled = true; };
  }, [missingKey, endpoint]);

  return version;
}

/** Test helper — forget the label requests made so far. */
export function resetLocationLabelRequests() {
  requests.clear();
}

export default useLocationLabels;
